import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as hi from "@heroicons/react/24/outline";

import { classNames, prettyDuration } from "../lib/utils";
import { IHealth, IRegionInfo } from "../lib/types";
import { ENVS, EXTRACT_JOB_STATUS, LOGICAL_REGIONS } from "../lib/consts";
import { Backend, BackendObj } from "../lib/backend";
import { selectEnv, selectIsSidebarLarge, selectSelfUrl, selectRegionInfo } from "../lib/scraper.slice";
import { IMetabaseDashboard } from "../lib/backend/extractions.types.generated";

import { BigButton } from "../components/BigButton";
import { Dropdown } from "../components/Dropdown";
export function AdminSettings() {
    const env = useSelector(selectEnv);
    const self_url = useSelector(selectSelfUrl);
    const is_sidebar_large = useSelector(selectIsSidebarLarge);
    const region_info = useSelector(selectRegionInfo);

    const [regions, setRegions] = useState<IRegionInfo[] | undefined>(undefined);
    const [health, setHealth] = useState<IHealth | undefined>(undefined);
    const [dashboards, setDashboards] = useState<IMetabaseDashboard[]>([]);

    useEffect(() => {
        Backend.getHealth()
            .then(setHealth);
        BackendObj.extractions.getAdminMetabaseDashboards({})
            .then(({ dashboards: new_dashboards }) => setDashboards(new_dashboards));
        Backend.getRegions()
            .then(data => { setRegions(data.regions); });
    }, []);

    const handle_switch_region = (region_row_key: string) => {
        window.location.href = `https://${regions?.find(region => region.row_key === region_row_key)?.domain}/admin/settings`;
    }

    let last_heartbeat_diff = 0;
    let build_diff = 0;
    if (health) {
        last_heartbeat_diff = (new Date().getTime() - new Date(health.last_heartbeat).getTime()) / 1000;
        build_diff = (new Date().getTime() - new Date(health.build_date).getTime()) / 1000;
    }

    return <div className={classNames("flex-row lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
        <div className="m-16 mb-6">
            <div className="flex flex-row items-end">
                <h2 className="text-xl font-semibold leading-7 text-gray-600 sm:truncate sm:text-3xl sm:tracking-tight">
                    Settings
                </h2>
                <div className="flex-grow" />
                <div className="flex flex-row items-">
                    {region_info && <Dropdown
                        id="region_select"
                        values={regions?.map((region) => `${region.logical_region} (${region.domain})`) ?? []}
                        ids={regions?.map((region) => region.row_key) ?? []}
                        selected={region_info.row_key}
                        onChange={handle_switch_region}
                    />}
                    {!region_info && <div className="text-gray-400 text-xs">No region selected or<br />running in local mode</div>}
                </div>
            </div>
            <div className="pt-5 border-b-4 border-sea_blue-700" />
        </div>

        <div className="mx-16 mt-10 mb-6">
            <h2 className="text-xl font-semibold leading-7 text-gray-600 sm:truncate sm:tracking-tight">
                Processed Jobs
            </h2>
        </div>
        <div className="mx-10 grid grid-cols-1 gap-4 sm:grid-cols-4">
            <BigButton title="Combined" icon={hi.InboxStackIcon} href="/admin/extraction-jobs/combined" />
            <BigButton title="Running" icon={hi.ClockIcon} href={`/admin/extraction-jobs/${EXTRACT_JOB_STATUS.running}`} />
            <BigButton title="Done" icon={hi.CheckIcon} href={`/admin/extraction-jobs/${EXTRACT_JOB_STATUS.done}`} />
            <BigButton title="Error" icon={hi.XMarkIcon} href={`/admin/extraction-jobs/${EXTRACT_JOB_STATUS.error}`} />
        </div>

        <div className="mx-16 mt-10 mb-6">
            <h2 className="text-xl font-semibold leading-7 text-gray-600 sm:truncate sm:tracking-tight">
                Analytics
            </h2>
        </div>
        <div className="mx-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {dashboards.map((dashboard, idx) => (
                <BigButton key={idx} title={dashboard.name} icon={hi.ChartBarIcon} href={`/admin/dashboard/${dashboard.uuid}`} />
            ))}
            <BigButton title="Demo Usage" icon={hi.ChartBarIcon} href="/admin/demo-usage" />
        </div>

        <div className="mx-16 mt-10 mb-6">
            <h2 className="text-xl font-semibold leading-7 text-gray-600 sm:truncate sm:tracking-tight">
                Administration
            </h2>
        </div>
        <div className="mx-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <BigButton title="Subscriptions" icon={hi.CogIcon} href="/admin/subscriptions" />
            <BigButton title="Referrals" icon={hi.CogIcon} href="/admin/referrals" />
            <BigButton title="Prompt Log" icon={hi.CogIcon} href="/admin/prompt-log" />
            <BigButton title="Import Template" icon={hi.CogIcon} href="/admin/import-template" />
            <BigButton title="Background Jobs" icon={hi.CogIcon} href="/admin/background-jobs" />
            <BigButton title="Database" icon={hi.ServerIcon} href="/admin/db" />
            <BigButton title="Debug Function Call" icon={hi.CodeBracketIcon} href="/admin/debug-function-call" />
            <BigButton title="Demo" icon={hi.CogIcon} href="/demo" />
            {env === ENVS.dev && <BigButton title="Extractions API" icon={hi.CodeBracketSquareIcon} href={`${self_url}/private/extractions/swagger`} />}
            {env === ENVS.dev && <BigButton title="Stripe API" icon={hi.CodeBracketSquareIcon} href={`${self_url}/private/stripe/swagger`} />}
            {env === ENVS.prod && region_info?.logical_region === LOGICAL_REGIONS.US && <BigButton title="Azure Dashboard" icon={hi.ChartPieIcon} href="https://portal.azure.com/#@nordoon.ai/dashboard/arm/subscriptions/8c060d04-e7cc-47f0-a677-e1668652e290/resourceGroups/rg-prod-eastus-app-nordoon/providers/Microsoft.Portal/dashboards/3a7b5229-7f01-49e7-ad5c-ed40139f31bb" />}
            {env === ENVS.prod && region_info?.logical_region === LOGICAL_REGIONS.EU && <BigButton title="Azure Dashboard" icon={hi.ChartPieIcon} href="https://portal.azure.com/#@nordoon.ai/dashboard/arm/subscriptions/8c060d04-e7cc-47f0-a677-e1668652e290/resourceGroups/rg-prod-gwc-app-nordoon/providers/Microsoft.Portal/dashboards/e1c8fe5e-f9c9-4a88-9398-a6796172c1aa" />}
            <BigButton title="Azure Dashboard Docs" icon={hi.CodeBracketSquareIcon} href="https://github.com/extraktAI/extraktAI/blob/main/docs/azure_dashboard.md" />
        </div>

        <div className="mx-16 mt-10 mb-6">
            <h2 className="text-xl font-semibold leading-7 text-gray-600 sm:truncate sm:tracking-tight">
                Runtime Information
            </h2>
        </div>
        <div className="mx-10 text-gray-400 text-xs">
            <table>
                <tbody>
                    <tr>
                        <td className="pr-4">Current Server Time:</td>
                        <td className="font-mono">{health ? health.now_date : "Loading..."}</td>
                    </tr>
                    <tr>
                        <td className="pr-4">Last Heartbeat</td>
                        <td className="font-mono">{health ? health.last_heartbeat : "Loading..."} ({prettyDuration(last_heartbeat_diff)} ago)</td>
                    </tr>
                    <tr>
                        <td className="pr-4">Last Start Time:</td>
                        <td className="font-mono">{health ? health.start_date : "Loading..."} ({health ? prettyDuration(health.uptime_seconds) : "Loading..."} ago)</td>
                    </tr>
                    <tr>
                        <td className="pr-4">Build Date:</td>
                        <td className="font-mono">{health ? health.build_date : "Loading..."} ({prettyDuration(build_diff)} ago)</td>
                    </tr>
                    <tr>
                        <td className="pr-4">Uptime:</td>
                        <td className="font-mono">{health ? prettyDuration(health.uptime_seconds) : "Loading..."}</td>
                    </tr>
                    <tr>
                        <td className="pr-4">Commit Hash:</td>
                        <td className="font-mono">{health ? health.commit_hash : "Loading..."}</td>
                    </tr>
                    <tr>
                        <td className="pr-4">DB Version:</td>
                        <td className="font-mono">{health ? health.db_version : "Loading..."}</td>
                    </tr>
                    <tr>
                        <td className="pr-4">Env:</td>
                        <td className="font-mono">{env}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>;
};
